import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Toast } from 'primereact/toast';
import { useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';

export default function UserLoyalty() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [txnTab, setTxnTab] = useState(0);

  useEffect(() => {
    dispatch(updatepageName('user_loyalty'));
    loadUsers();
  }, [dispatch]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await CustomAxios.get('get_users/');
      setUsers(res.data?.users || []);
    } catch (e) {
      showToast('error', 'Error', 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users.slice(0, 50);
    return users.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      const phone = String(u.mobileno || u.mobile || '');
      const email = String(u.email || '').toLowerCase();
      return name.includes(q) || phone.includes(q) || email.includes(q) || String(u.id).includes(q);
    }).slice(0, 50);
  }, [users, search]);

  const loadUserLoyalty = async (user) => {
    setSelectedUser(user);
    setLoadingDetail(true);
    setWallet(null);
    setTransactions([]);
    try {
      const [walletRes, txnRes] = await Promise.all([
        CustomAxios.get(`loyalty_wallet/${user.id}/`),
        CustomAxios.get(`loyalty_transactions/${user.id}/`),
      ]);
      setWallet(walletRes.data?.loyalty_wallet || walletRes.data || null);
      setTransactions(txnRes.data?.loyalty_transactions || []);
    } catch (e) {
      showToast('error', 'Error', 'Failed to load loyalty data for user');
    } finally {
      setLoadingDetail(false);
    }
  };

  const filteredTxns = useMemo(() => {
    if (txnTab === 0) return transactions.filter((t) => t.transaction_type === 'EARN');
    if (txnTab === 1) return transactions.filter((t) => t.transaction_type === 'REDEEM');
    return transactions;
  }, [transactions, txnTab]);

  const userLabel = (u) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || `User #${u.id}`;
    const phone = u.mobileno || u.mobile || '';
    return phone ? `${name} (${phone})` : name;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toast ref={toast} />
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        User Loyalty Overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search a customer to view point balance, earn history, and redemption history.
      </Typography>

      <TextField
        fullWidth
        placeholder="Search by name, phone, email, or user ID"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: 480 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingUsers && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loadingUsers && filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No users match your search
                </TableCell>
              </TableRow>
            )}
            {filteredUsers.map((u) => (
              <TableRow
                key={u.id}
                hover
                selected={selectedUser?.id === u.id}
                sx={{ cursor: 'pointer' }}
                onClick={() => loadUserLoyalty(u)}
              >
                <TableCell>{u.id}</TableCell>
                <TableCell>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                <TableCell>{u.mobileno || u.mobile || '—'}</TableCell>
                <TableCell>{u.email || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedUser && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {userLabel(selectedUser)}
          </Typography>

          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Chip label={`Available: ${wallet?.available_points ?? 0} pts`} color="primary" />
                <Chip label={`Total earned: ${wallet?.total_points ?? 0} pts`} variant="outlined" />
                <Chip label={`Used: ${wallet?.used_points ?? 0} pts`} variant="outlined" />
                <Chip label={`Expired: ${wallet?.expired_points ?? 0} pts`} variant="outlined" />
              </Box>

              <Tabs value={txnTab} onChange={(_, v) => setTxnTab(v)} sx={{ mb: 2 }}>
                <Tab label={`Earn (${transactions.filter((t) => t.transaction_type === 'EARN').length})`} />
                <Tab label={`Redeem (${transactions.filter((t) => t.transaction_type === 'REDEEM').length})`} />
                <Tab label={`All (${transactions.length})`} />
              </Tabs>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>₹ Value</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTxns.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No transactions
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredTxns.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>
                          {t.created_on ? new Date(t.created_on).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>{t.transaction_type}</TableCell>
                        <TableCell>{t.points}</TableCell>
                        <TableCell>
                          {t.redemption_value_inr != null ? `₹${t.redemption_value_inr}` : '—'}
                        </TableCell>
                        <TableCell>{t.order_id || t.order || '—'}</TableCell>
                        <TableCell>{t.description || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
